import { usePermissions } from '@/features/auth';
import type { Permission } from '@/shared/auth/permissions';

/**
 * Permission needed to read an item's History, by entity type — mirrors
 * `AdminHistoryPolicy.ReadPermission` in UT system (D-26). The backend
 * enforces it and removes the recorded values the role may not see.
 */
export const HISTORY_READ_PERMISSION: Record<string, Permission> = {
  Brand: 'catalogue.read',
  Denomination: 'catalogue.read',
  Category: 'catalogue.read',
  CategoryOrder: 'catalogue.read',
  BrandOrder: 'catalogue.read',
  DenominationOrder: 'catalogue.read',
  RegionOrder: 'catalogue.read',
  Region: 'catalogue.read',
  ProviderMapping: 'catalogue.read',
  CountryAvailability: 'catalogue.read',
  Provider: 'providers.read',
  Country: 'pricing.read',
  Currency: 'pricing.read',
  PaymentChannel: 'pricing.read',
  PricingParameter: 'pricing.read',
  PricingParameterSet: 'pricing.read',
  FxRate: 'pricing.read',
  Customer: 'customers.read',
  Complaint: 'complaints.manage',
  Order: 'orders.details.read',
  Refund: 'refunds.request',
  StaffUser: 'users.manage',
};

/**
 * A permission that also opens an item's History, without the entries' reasons — mirrors
 * `AdminHistoryPolicy.LimitedReadPermission`: a Partner reads complaint History with complaints.read.
 */
export const HISTORY_LIMITED_READ_PERMISSION: Record<string, Permission> = {
  Complaint: 'complaints.read',
};

/** Whether the History of items of this type may be shown to the signed-in staff member. */
export function useCanSeeHistory(entityType: string) {
  const { can } = usePermissions();
  const permission = HISTORY_READ_PERMISSION[entityType];
  const limited = HISTORY_LIMITED_READ_PERMISSION[entityType];
  return (permission !== undefined && can(permission)) || (limited !== undefined && can(limited));
}
